import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFieldArray, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { uploadFloorPlan } from 'utils/validate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';
import { addFloorPlan, createLocation, getLocationDetail } from 'services/locations';
import { RoomInfo } from 'types/room';
import * as pdfjsLib from 'pdfjs-dist';
import imageCompression from 'browser-image-compression';

type FormCreateLocation = {
  names: {
    value: string;
    floorPlan: File;
  }[];
};

type compressionoptions = {
  maxSizeMB: number;
  maxWidthOrHeight: number;
};

export const useInjection = () => {
  const navigate = useNavigate();
  const { clientId, locationId, clientName } = useParams();
  const { setFlash } = useFlash();
  const [searchParams] = useSearchParams();
  const level = searchParams.get('level');

  const {
    handleSubmit,
    control,
    formState: { errors }
  } = useForm<FormCreateLocation>({ resolver: yupResolver(uploadFloorPlan) });

  const {
    fields: fieldNames,
    append,
    remove
  } = useFieldArray({
    control,
    name: 'names' as never
  });

  const [roomInfo, setRoomInfo] = useState<RoomInfo>([]);
  const [title, setTitle] = useState<{
    title: string;
    textButton: string;
  }>({
    title: '',
    textButton: ''
  });

  pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
    'pdfjs-dist/build/pdf.worker.min.mjs',
    import.meta.url
  ).toString();

  useEffect(() => {
    if (level === '4') {
      setTitle({
        title: 'Floor Plan',
        textButton: 'Add Floor Plan'
      });
    } else if (level === '5') {
      setTitle({
        title: 'Room',
        textButton: 'Add Room'
      });
    }
  }, [level]);

  const paths: RoomInfo = useMemo(() => {
    const pathInit = [
      { name: 'PDF Mapping', level: 999, onClick: () => navigate('/pdfmapping') },
      {
        name: clientName || '',
        id: Number(clientId),
        isClient: true,
        level: 998,
        onClick: () => navigate(`/pdfmapping/uploadplan/${clientName}/${clientId}/location`)
      }
    ];
    const locationInit = roomInfo.find((item) => item.id === Number(locationId));
    if (!locationInit)
      return [...pathInit, { name: title.textButton, level: 999, onClick: () => {} }];
    return [
      ...pathInit,
      ...roomInfo
        .sort((a, b) => a.level - b.level)
        .map((item) => {
          return {
            ...item,
            onClick: () =>
              navigate(`/pdfmapping/uploadplan/${clientName}/${clientId}/location/${item.id}`)
          };
        }),
      { name: title.textButton, level: 999, onClick: () => {} }
    ];
  }, [clientId, clientName, locationId, navigate, roomInfo, title]);

  const handleGetInitLocations = useCallback(async () => {
    if (!locationId || !level) {
      return;
    }
    const data = (await getLocationDetail(Number(locationId))) as {
      location: Location;
      room: RoomInfo;
    };
    setRoomInfo(data.room);
  }, [locationId, level]);

  useEffect(() => {
    handleGetInitLocations();
  }, [handleGetInitLocations]);

  const handleCreateClient = useCallback(
    async (value: FormCreateLocation) => {
      const payload = {
        names: value.names.map((item) => item.value),
        clientId: Number(clientId) || undefined,
        parentId: Number(locationId) || undefined,
        level: Number(level) || undefined
      };
      const locationcreatedid = await createLocation(payload);
      await Promise.all(
        locationcreatedid.data.locations.map(
          (location: (typeof locationcreatedid.data.locations)[number], index: number) =>
            addFloorPlan({
              id: Number(location.id),
              file: value.names[index].floorPlan,
              name: value.names[index].value
            })
        )
      );

      setFlash({ type: 'success', message: 'Upload floor plan successful' });

      const newPath = location.pathname.replace(/\/upload$/, '');
      navigate(newPath);
    },
    [clientId, locationId, navigate, setFlash, level]
  );

  const uploadfloorplan = async (file: File) => {
    if (!file) return;
    if (file?.type === 'image/png') {
      const compressimg = await imagecompress(file);
      return compressimg;
    } else if (file?.type === 'application/pdf') {
      const imgfile = await convertPdfToImage(file);
      return imgfile;
    }
  };

  const convertPdfToImage = async (file: File) => {
    if (!file) return;
    try {
      const pdfBuffer = await file.arrayBuffer();
      const pdf = await pdfjsLib.getDocument({ data: new Uint8Array(pdfBuffer) }).promise;
      const page = await pdf.getPage(1);
      const viewport = page.getViewport({ scale: 3 });
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      if (!context) {
        throw new Error('Canvas is not supported');
      }
      canvas.width = viewport.width;
      canvas.height = viewport.height;
      await page.render({ canvasContext: context, viewport, canvas }).promise;
      const image: File = await new Promise((resolve) => {
        canvas.toBlob((blob) => {
          if (!blob) {
            throw new Error('Canvas conversion to blob failed');
          }
          const fileName = file.name.substring(0, file.name.lastIndexOf('.')) + '.png';
          const fileObj = new File([blob], fileName, { type: 'image/png' });
          resolve(fileObj);
        }, 'image/png');
      });
      const img = await imagecompress(image);
      return img;
    } catch (error) {
      console.error(error);
    }
  };

  const imagecompress = async (image: File) => {
    const options: compressionoptions = {
      maxSizeMB: Number(process.env.REACT_APP_MAX_FILE_SIZE_MB) || 1,
      maxWidthOrHeight: Number(process.env.REACT_APP_MAX_WIDTHORHEIGHT_PX) || 1920
    };
    const img = await imageCompression(image, options);
    return img;
  };
  const { asyncCallback } = useAsyncCallback(handleCreateClient, []);
  return {
    backToClient: () => navigate(`${location.pathname.replace(/\/upload$/, '')}`),
    handleSubmit,
    errors,
    onCreateLocation: asyncCallback,
    uploadfloorplan,
    fieldNames,
    append,
    remove,
    control,
    paths,
    title
  };
};
